<template>
    <Teleport to="body">
        <div class="context-menu-background explorer-ignore" @click="close" @contextmenu.prevent="close" v-if="data.visible"></div>

        <Transition name="context-fade-scale">
            <ul
                v-if="data.visible"
                ref="menuRef"
                class="context-menu explorer-ignore"
                :style="{ top: `${position.y}px`, left: `${position.x}px` }"
                @click.stop
            >
                <li
                    v-for="(item, idx) in items"
                    :key="idx"
                    :class="{ disabled: item.disabled }"
                    @click="handleClick(item)"
                >
                    <Icon v-if="item.icon" :icon="item.icon" :sprite="item.sprite" />

                    <span>
                        {{ item.name }}
                    </span>
                </li>
            </ul>
        </Transition>
    </Teleport>
</template>

<script setup>
    import { ref, nextTick, computed, reactive, onMounted, onUnmounted, watch } from 'vue';
    import Icon from '🔗/components/ui/Icon.vue';


    const props = defineProps({
        event: { type: Object, required: true },
        items: { type: [Array, Object], required: true },
        onClose: { type: Function, required: true },
    });

    const items = computed(() => props.items?.value || []);

    watch(() => items.value, () => {
        setTimeout(() => {
            adjustPosition();
        }, 100)
    });

    const position = ref({ x: 0, y: 0 });
    const menuRef = ref(null);

    const data = reactive({
        visible: false
    });

    const open = async () => {
        data.visible = true;

        position.value = { x: props.event.clientX, y: props.event.clientY };
        await nextTick();
        adjustPosition();
    };

    const close = () => {
        data.visible = false;
        props.onClose?.();
    };

    const handleClick = (item) => {
        if (item.disabled) {
            return;
        }

        if (typeof item.onClick === 'function') {
            item.onClick();
        }

        close();
    };

    const adjustPosition = () => {
        const menu = menuRef.value;
        if (!menu) {
            return;
        }

        const rect = menu.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let newX = position.value.x;
        let newY = position.value.y;

        // если уходит вправо
        if (rect.right > viewportWidth) {
            newX = Math.max(0, viewportWidth - rect.width - 8);
        }

        // если уходит вниз
        if (rect.bottom > viewportHeight) {
            newY = Math.max(0, viewportHeight - rect.height - 8);
        }

        position.value = { x: newX, y: newY };
    };

    onMounted(() => {
        open();

        const onKey = (e) => {
            if (e.key === 'Escape') {
                close();
            }
        };
        window.addEventListener('keydown', onKey);

        onUnmounted(() => {
            window.removeEventListener('keydown', onKey);
        });

        setTimeout(() => {
            if (!items.value?.length) {
                close();
            }
        })
    });
</script>

<style scoped lang="scss">
    .context-menu-background {
        position: fixed;
        left: 0;
        top: 0;
        bottom: 0;
        right: 0;
        content: "";
        background-color: rgba(255, 255, 255, 0.01);
        z-index: 999;
    }

    .context-menu {
        position: fixed;
        min-width: 192px;

        border-radius: 10px;
        padding: 8px 0;
        @include sub-bg;
        max-height: 40vh;
        z-index: 1000;
        list-style: none;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0;
        overflow-y: auto;
        overflow-x: hidden;
        @include scrollbar(var(--white-10), var(--dark-20), 6px);

        li {
            display: flex;
            flex-direction: row;
            padding: 10px 20px;
            align-items: center;
            justify-content: space-between;
            font: var(--font-body-2);
            color: var(--dark-500);
            cursor: pointer;
            gap: 20px;
            min-width: 192px;
            flex-shrink: 0;

            .skeleton-wrapper {
                width: 30px;
                height: 30px;
                margin: -7px;
                border-radius: 8px;
                overflow: hidden;
            }

            svg {
                fill: var(--dark-20);
                stroke: var(--dark-80);
                flex-shrink: 0;
                width: 16px;
                height: 16px;
                flex-shrink: 0;
            }

            span {
                margin-right: auto;
            }

            &:hover {
                color: var(--primary-500);

                & > svg {
                    fill: var(--primary-500);
                    stroke: var(--tertiary-500);
                }

                & > span {
                    color: var(--primary-500);
                }
            }

            &.disabled {
                color: var(--dark-20);
                cursor: default;
                & > svg {
                    fill: var(--dark-20);
                    stroke: var(--dark-20);
                }
                & > span {
                    color: var(--dark-20);
                }
            }
        }
    }
</style>

<style lang="scss">
    .context-fade-scale-enter-active,
    .context-fade-scale-leave-active {
        transition: opacity 0.2s cubic-bezier(.4,0,.6,1),
              transform 0.2s cubic-bezier(.4,0,.6,1);
        transform-origin: top left; // меню открывается из угла
    }

    .context-fade-scale-enter-from {
        opacity: 0;
        transform: scale(0.8);
    }

    .context-fade-scale-enter-to {
        opacity: 1;
        transform: scale(1);
    }

    .context-fade-scale-leave-from {
        opacity: 1;
        transform: scale(1);
    }

    .context-fade-scale-leave-to {
        opacity: 0;
        transform: scale(0.8);
    }
</style>
